import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, tap } from 'rxjs';

import {
  TellentAssistantAguiClient,
  type CurrentThreadMessages,
} from '@core/tellent-assistant-data';
import { aguiMessagesToChatMessages, chatMessagesToAgui } from '@core/tellent-assistant-util';

import { TellentAssistantThreadsFacade } from '../tellent-assistant-threads.facade';
import { AssistantRunStore } from './assistant-run.store';

@Injectable({ providedIn: 'root' })
export class AssistantRunService {
  private readonly store = inject(AssistantRunStore);
  private readonly client = inject(TellentAssistantAguiClient);
  private readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  private readonly destroyRef = inject(DestroyRef);

  public readonly messages = this.store.messages;
  public readonly runStatus = this.store.runStatus;
  public readonly currentThreadId = this.store.currentThreadId;
  public readonly lastError = this.store.lastError;
  public readonly isRunning = this.store.isRunning;

  constructor() {
    this.client.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(msgs => this.store.replaceMessages(aguiMessagesToChatMessages(msgs)));
    this.client.runError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(err => this.store.markRunError(err.message));
    this.client.runFinalized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.store.runStatus() === 'running') this.store.markRunEnd();
      this.threadsFacade.loadThreads();
    });

    const syncFromCurrentThread = rxMethod<CurrentThreadMessages | null>(
      (source$: Observable<CurrentThreadMessages | null>) =>
        source$.pipe(
          tap((t: CurrentThreadMessages | null) => {
            if (t === null || t.threadId !== this.store.currentThreadId()) return;
            this.client.setMessages(chatMessagesToAgui(t.messages));
          }),
        ),
    );
    syncFromCurrentThread(toObservable(this.threadsFacade.currentThread));
  }

  public send(text: string): void {
    if (!this.store.currentThreadId()) {
      const id = crypto.randomUUID();
      this.store.resetForThread(id);
      this.client.setThreadId(id);
    }
    this.client.addUserMessage(text);
    this.store.markRunStart();
    void this.client.run(crypto.randomUUID());
  }

  public cancelRun(): void {
    this.client.cancelRun();
    if (this.store.runStatus() === 'running') this.store.markRunEnd();
  }

  public resetForThread(threadId: string): void {
    this.store.resetForThread(threadId);
    this.client.setThreadId(threadId);
    this.client.setMessages([]);
  }
}
